import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import { makeStyles } from '@material-ui/core';
import React, { useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { treeMapColors } from '../../../../core/config/mainTheme';
import { useFilterDispatch } from '../../shared/FinancialDataContext/FinancialDataContext';
import { FinancialDataContextActionTypes } from '../../shared/FinancialDataContext/FinancialDataContext.types';

interface Props {
  keywordsData: { name: string; value: number }[];
  id: number;
  onClick: () => void;
}

export const MinimizedActivityKeywordsChart: React.FC<Props> = ({ keywordsData, id, onClick }) => {
  const dispatch = useFilterDispatch();
  const classes = useStyles();
  const data = mapDataColors(keywordsData);
  const { t } = useTranslation();

  function onIncludeClick(keyword: string) {
    dispatch({
      type: FinancialDataContextActionTypes.Add,
      payload: 'activityKeywords',
    });
    dispatch({
      type: FinancialDataContextActionTypes.AddIncludeKeywordsFilterValues,
      payload: keyword,
    });
  }

  useLayoutEffect(() => {
    const chart = am4core.create(`chartdiv${id}`, am4charts.TreeMap);
    populateChartProps(chart, data, onIncludeClick);

    const chartNav = am4core.create(`chartNavdiv${id}`, am4charts.TreeMap);
    populateChartProps(
      chartNav,
      [{ name: t('activityKeywords:dialog:expandKeywords'), value: 1, color: treeMapColors?.[0] }],
      null,
    );

    return () => {
      chart.dispose();
      chartNav.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keywordsData]);

  return (
    <>
      <div id={`chartdiv${id}`} className={classes.containerMain}></div>
      <div id={`chartNavdiv${id}`} onClick={onClick} className={classes.containerNav}></div>
    </>
  );
};

function populateChartProps(chart: am4charts.TreeMap, data: any[], onIncludeClick: any) {
  chart.data = data;
  chart.sorting = 'descending';
  chart.responsive.enabled = true;

  const chartItem = chart.seriesTemplates.create('0');

  const label = chartItem.bullets.push(new am4charts.LabelBullet());
  label.locationY = 0.5;
  label.locationX = 0.5;
  label.label.text = '{name}';
  label.label.fontSize = 12;
  label.label.fill = am4core.color('#fff');

  const column = chartItem.columns.template;
  column.tooltipText = onIncludeClick ? '{name}: {value} of results in search' : '';
  column.cursorOverStyle = am4core.MouseCursorStyle.pointer;
  label.cursorOverStyle = am4core.MouseCursorStyle.pointer;

  chart.dataFields.value = 'value';
  chart.dataFields.name = 'name';
  chart.dataFields.color = 'color';
  chart.zoomable = false;

  if (onIncludeClick) {
    column.events.on('hit', (e) => {
      const context: any = e.target.dataItem!.dataContext;
      onIncludeClick(context.name as string);
    });

    label.events.on('hit', (e) => {
      const context: any = e.target.dataItem!.dataContext;
      onIncludeClick(context.name as string);
    });
  }
}

function mapDataColors(
  data: { name: string; value: number }[],
): { name: string; value: number; color: string }[] {
  data.sort((a, b) => (a.value > b.value ? -1 : 1));
  return data.slice(0, Math.min(data.length, 6)).map((d, i) => ({
    ...d,
    color: treeMapColors?.[i] ?? treeMapColors[treeMapColors.length - 1],
  }));
}

const useStyles = makeStyles(() => ({
  containerMain: {
    marginTop: '10px',
    height: '100px',
  },
  containerNav: {
    height: '50px',
  },
}));
